import { ErrorMessage, Field, FieldArray } from 'formik';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, FormControl, Row } from 'react-bootstrap';
import Editor from '../../../../../components/editor/Editor';
import { FormLabel, FormStepHeader } from '../../../../../components/forms';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import { useGetRecipesSimpleQuery } from '../../../../../services/endpoints/content/recipe';
import { StepProps } from './AddEditLessonPlan';

export const LessonPlanForm = ({ touched, errors, values, setFieldValue, readOnly }: StepProps) => {
  const [recipesOptions, setRecipesOptions] = useState<{ value: number; label: string }[]>([]);
  const { data: recipes, error: recipesError } = useGetRecipesSimpleQuery();

  useEffect(() => {
    if (recipes) {
      let opts = recipes.map((rec: { id: number; title: string }) => ({
        value: rec.id,
        label: rec.title
      }));
      setRecipesOptions(opts);
    }
  }, [recipes]);

  return (
    <Row>
      <Col xs={12}>
        <FormStepHeader title="Details" showRequiredLegend={!readOnly} />
      </Col>
      <Col
        xs={12}
        lg={6}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="name"
        >
          <FormLabel required>Name</FormLabel>
          <Field
            name="name"
            type="text"
            as={Form.Control}
            isInvalid={touched.name && !!errors.name}
            aria-required
          />
          <ErrorMessage name="name">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={6}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="publish_status"
        >
          <FormLabel required>Publish Status</FormLabel>
          <Form.Control
            as="select"
            name="user_type"
            value={values?.publish_status}
            onChange={(e) => setFieldValue!('publish_status', e.target.value)}
            isInvalid={touched.publish_status && !!errors.publish_status}
            aria-required
          >
            <option>Select</option>
            <option value="published">Published</option>
            <option value="unpublished">Unpublished</option>
          </Form.Control>
          <ErrorMessage name="publish_status">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <FieldArray
        name="recipes"
        render={(arrayHelpers) => (
          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="recipes"
          >
            <Form.Label className="h4">Recipes</Form.Label>
            <br />
            <div className="multi-content-scroll mt-2">
              <Row>
                {values?.recipes && values?.recipes.length > 0 ? (
                  values.recipes.map((recipe, index) => {
                    return (
                      <Col
                        key={index}
                        lg={3}
                        className="mb-2"
                      >
                        <Card border="primary">
                          {typeof errors.recipes === 'string' ? <div>{errors.recipes}</div> : null}

                          <Card.Body>
                            <Col className="ms-1">
                              <Row>
                                <Col
                                  xs={12}
                                  className="pe-1"
                                >
                                  <Form.Group
                                    className="mb-lg-3 mb-1"
                                    controlId={`recipes.${index}.id`}
                                  >
                                    {recipesOptions.length > 0 ? (
                                      <FormSelect
                                        name={`recipes.${index}.id`}
                                        label="Recipe Name"
                                        options={recipesOptions}
                                        loadingError={
                                          recipesError ? 'Error loading recipes' : undefined
                                        }
                                      />
                                    ) : (
                                      <>{recipe.name}</>
                                    )}
                                  </Form.Group>

                                  <Form.Group controlId={`recipes.${index}.is_primary`}>
                                    <FormLabel>
                                      <Field
                                        name={`recipes.${index}.is_primary`}
                                        type="checkbox"
                                        as={Form.Check}
                                        label={'is primary?'}
                                      />
                                    </FormLabel>
                                  </Form.Group>

                                  <Button
                                    variant="primary"
                                    size="sm"
                                    className="btn-box-shadow text-uppercase border-0"
                                    style={{ width: 84, background: '#B81F69', fontSize: '.75rem' }}
                                    onClick={() => arrayHelpers.remove(index)}
                                  >
                                    Remove
                                  </Button>
                                </Col>
                              </Row>
                            </Col>
                            {/* <Col hidden={index === 0} xs={1}>
                        <CloseButton onClick={() => arrayHelpers.remove(index)} />
                      </Col> */}
                          </Card.Body>
                          {/* { index < values.recipes.length - 1 ? <hr /> : <></>} */}
                        </Card>
                      </Col>
                    );
                  })
                ) : (
                  <></>
                )}
              </Row>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-link fw-bolder text-uppercase link-primary link-underline-opacity-0"
              onClick={() =>
                arrayHelpers.insert(values?.recipes.length ?? 0, {
                  id: ''
                })
              } // insert an empty string at a position
            >
              + Add New Lesson Plan Recipe
            </button>
          </Form.Group>
        )}
      />

      <h6
        className="fw-bold mb-3"
        style={{ fontSize: '1.125rem', color: '#5E6469' }}
      >
        Family Meal Plan Stats
      </h6>
      <Col
        xs={12}
        lg={6}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="cook_time"
        >
          <FormLabel required>Cook Time</FormLabel>
          <Field
            name="cook_time"
            type="text"
            as={Form.Control}
            isInvalid={touched.cook_time && !!errors.cook_time}
            aria-required
          />
          <ErrorMessage name="cook_time">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={6}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="prep_time"
        >
          <FormLabel required>Prep Time</FormLabel>
          <Field
            name="prep_time"
            type="text"
            as={Form.Control}
            isInvalid={touched.prep_time && !!errors.prep_time}
            aria-required
          />
          <ErrorMessage name="prep_time">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={6}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="min_served"
        >
          <FormLabel required>Min Served</FormLabel>
          <Field
            name="min_served"
            type="text"
            as={Form.Control}
            isInvalid={touched.min_served && !!errors.min_served}
            aria-required
          />
          <ErrorMessage name="min_served">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={6}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="max_served"
        >
          <FormLabel required>Max Served</FormLabel>
          <Field
            name="max_served"
            type="text"
            as={Form.Control}
            isInvalid={touched.max_served && !!errors.max_served}
            aria-required
          />
          <ErrorMessage name="max_served">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>
      <h6
        className="fw-bold mb-3"
        style={{ fontSize: '1.125rem', color: '#5E6469' }}
      >
        Lesson Plan Stats
      </h6>
      <Col
        xs={12}
        lg={6}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="lesson_cook_time"
        >
          <FormLabel required>Cook Time</FormLabel>
          <Field
            name="lesson_cook_time"
            type="text"
            as={Form.Control}
            isInvalid={touched.lesson_cook_time && !!errors.lesson_cook_time}
            aria-required
          />
          <ErrorMessage name="lesson_cook_time">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={6}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="lesson_prep_time"
        >
          <FormLabel required>Prep Time</FormLabel>
          <Field
            name="lesson_prep_time"
            type="text"
            as={Form.Control}
            isInvalid={touched.lesson_prep_time && !!errors.lesson_prep_time}
            aria-required
          />
          <ErrorMessage name="lesson_prep_time">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={6}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="lesson_min_served"
        >
          <FormLabel required>Min Served</FormLabel>
          <Field
            name="lesson_min_served"
            type="text"
            as={Form.Control}
            isInvalid={touched.lesson_min_served && !!errors.lesson_min_served}
            aria-required
          />
          <ErrorMessage name="lesson_min_served">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={6}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="lesson_max_served"
        >
          <FormLabel required>Max Served</FormLabel>
          <Field
            name="lesson_max_served"
            type="text"
            as={Form.Control}
            isInvalid={touched.lesson_max_served && !!errors.lesson_max_served}
            aria-required
          />
          <ErrorMessage name="lesson_max_served">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <h6
        className="fw-bold mb-3"
        style={{ fontSize: '1.125rem', color: '#5E6469' }}
      >
        Lists
      </h6>

      <Col
        xs={12}
        lg={12}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="shopping_list_items"
        >
          <FormLabel required>Shopping list items</FormLabel>
          <Field
            name="shopping_list_items"
            as="textarea"
            className={`form-control ${touched.shopping_list_items && !!errors.shopping_list_items ? 'is-invalid' : ''}`}
            aria-required
          />
          <ErrorMessage name="shopping_list_items">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <h6
        className="fw-bold mb-3"
        style={{ fontSize: '1.125rem', color: '#5E6469' }}
      >
        Instructor Notes
      </h6>

      <Col
        xs={12}
        lg={12}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="notes"
        >
          <FormLabel>Lesson Plan Add-ins & Tidbits</FormLabel>

          <Field
            name="notes"
            component={Editor}
            className="form-control"
            isInvalid={touched.notes && !!errors.notes}
          />

          <ErrorMessage name="notes">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={12}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="before_class"
        >
          <FormLabel>Before class</FormLabel>

          <Field
            name="before_class"
            component={Editor}
            className="form-control"
            isInvalid={touched.before_class && !!errors.before_class}
          />

          <ErrorMessage name="before_class">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={12}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="at_school"
        >
          <FormLabel>At School</FormLabel>
          <Field
            name="at_school"
            component={Editor}
            className="form-control"
            isInvalid={touched.at_school && !!errors.at_school}
          />

          <ErrorMessage name="at_school">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={12}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="welcome"
        >
          <FormLabel>Welcome Your Students</FormLabel>
          <Field
            name="welcome"
            component={Editor}
            className="form-control"
            isInvalid={touched.welcome && !!errors.welcome}
          />
          <ErrorMessage name="welcome">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <h6
        className="fw-bold mb-3"
        style={{ fontSize: '1.125rem', color: '#5E6469' }}
      >
        Timeline
      </h6>

      <Col
        xs={12}
        lg={12}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="timeline_00"
        >
          <FormLabel>0-10 Minutes</FormLabel>
          <Field
            name="timeline_00"
            as="textarea"
            className={`form-control ${touched.timeline_00 && !!errors.timeline_00 ? 'is-invalid' : ''}`}
          />
          <ErrorMessage name="timeline_00">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={12}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="timeline_10"
        >
          <FormLabel>10-25 Minutes</FormLabel>
          <Field
            name="timeline_10"
            as="textarea"
            className={`form-control ${touched.timeline_10 && !!errors.timeline_10 ? 'is-invalid' : ''}`}
          />
          <ErrorMessage name="timeline_10">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={12}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="timeline_25"
        >
          <FormLabel>25-35 Minutes</FormLabel>
          <Field
            name="timeline_25"
            as="textarea"
            className={`form-control ${touched.timeline_25 && !!errors.timeline_25 ? 'is-invalid' : ''}`}
          />
          <ErrorMessage name="timeline_25">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={12}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="timeline_35"
        >
          <FormLabel>35-45 Minutes</FormLabel>
          <Field
            name="timeline_35"
            as="textarea"
            className={`form-control ${touched.timeline_35 && !!errors.timeline_35 ? 'is-invalid' : ''}`}
          />
          <ErrorMessage name="timeline_35">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>

      <Col
        xs={12}
        lg={12}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="timeline_45"
        >
          <FormLabel>45-60 Minutes</FormLabel>
          <Field
            name="timeline_45"
            as="textarea"
            className={`form-control ${touched.timeline_45 && !!errors.timeline_45 ? 'is-invalid' : ''}`}
          />
          <ErrorMessage name="timeline_45">
            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
          </ErrorMessage>
        </Form.Group>
      </Col>
    </Row>
  );
};
